import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../redux/helpers/axios";

/**
 * @author
 * @function ElevenCarts
 *
 * Admin: customers' 11.11 carts (items added but not yet checked out).
 **/

const API = {
  list: "/eleven/admin/carts",
  one: (id) => `/eleven/admin/carts/${id}`,
  item: (cartId, itemId) => `/eleven/admin/carts/${cartId}/items/${itemId}`,
};

const formatPrice = (n) =>
  new Intl.NumberFormat("en-BD", {
    style: "currency",
    currency: "BDT",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const timeAgo = (d) => {
  if (!d) return "";
  const s = Math.max(0, (Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

/* ---------- small UI ---------- */

const Img = ({ src, className = "" }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  if (!src || broken)
    return (
      <div
        className={`bg-gray-100 rounded-md border border-gray-200 ${className}`}
      />
    );
  return (
    <img
      referrerPolicy="no-referrer"
      src={src}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
      className={`object-cover rounded-md border border-gray-200 ${className}`}
    />
  );
};

const Stat = ({ label, value, hint }) => (
  <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
    {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
  </div>
);

const Pagination = ({ p, onPage, disabled }) => {
  if (!p || p.totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 text-sm">
      <span className="text-gray-500">
        Page <b className="text-gray-800">{p.currentPage}</b> of {p.totalPages}{" "}
        · {p.total} total
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={disabled || !p.hasPreviousPage}
          onClick={() => onPage(p.currentPage - 1)}
          className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          ← Prev
        </button>
        <button
          type="button"
          disabled={disabled || !p.hasNextPage}
          onClick={() => onPage(p.currentPage + 1)}
          className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          Next →
        </button>
      </div>
    </div>
  );
};

const Toast = ({ toast, onClose }) => {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  return (
    <div className="fixed z-[60] top-4 right-4 max-w-sm">
      <div
        className={`rounded-lg px-4 py-3 shadow-lg text-sm text-white ${
          toast.type === "error" ? "bg-red-600" : "bg-gray-900"
        }`}
      >
        {toast.msg}
      </div>
    </div>
  );
};

const Drawer = ({ open, onClose, title, children }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl h-full bg-gray-50 shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-800">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
};

/* ---------- cart detail ---------- */

const CartDetail = ({ cartId, onChanged, notify }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(API.one(cartId));
      setCart(data.cart);
    } catch (err) {
      notify("error", err.response?.data?.msg || "Failed to load cart");
    } finally {
      setLoading(false);
    }
  }, [cartId, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const removeItem = async (item) => {
    if (
      !window.confirm(`Remove "${item.product.productTitle}" from this cart?`)
    )
      return;
    setBusy(item.id);
    try {
      const { data } = await axiosInstance.delete(API.item(cartId, item.id));
      notify("success", data.msg);
      await load();
      onChanged();
    } catch (err) {
      notify("error", err.response?.data?.msg || "Failed to remove item");
    } finally {
      setBusy(null);
    }
  };

  const clearCart = async () => {
    if (!window.confirm("Remove ALL items from this customer's cart?")) return;
    setBusy("all");
    try {
      const { data } = await axiosInstance.delete(API.one(cartId));
      notify("success", data.msg);
      await load();
      onChanged();
    } catch (err) {
      notify("error", err.response?.data?.msg || "Failed to clear cart");
    } finally {
      setBusy(null);
    }
  };

  if (loading && !cart)
    return <p className="text-sm text-gray-500">Loading…</p>;
  if (!cart) return null;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <p className="text-xs text-gray-500">Customer</p>
        <p className="text-sm font-semibold text-gray-900">{cart.user?.name}</p>
        <p className="text-xs text-gray-600">
          {cart.user?.email}
          {cart.user?.phone ? ` · ${cart.user.phone}` : ""}
        </p>
        <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
          <div>
            <p className="text-xs text-gray-500">Items</p>
            <p className="font-bold text-gray-900">{cart.items.length}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Quantity</p>
            <p className="font-bold text-gray-900">{cart.totalQty}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Value (current prices)</p>
            <p className="font-bold text-indigo-600">
              {formatPrice(cart.subtotal)}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-800">Items</h3>
          {cart.items.length > 0 && (
            <button
              type="button"
              onClick={clearCart}
              disabled={busy === "all"}
              className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-50"
            >
              {busy === "all" ? "Clearing…" : "Clear cart"}
            </button>
          )}
        </div>
        {cart.items.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-400">
            This cart is empty.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {cart.items.map((i) => (
              <li key={i.id} className="flex gap-3 px-4 py-3">
                <Img
                  src={i.variation?.variationImg || i.product.productImg}
                  className="w-14 h-14 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 line-clamp-2">
                    {i.product.productTitle}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {i.variation
                      ? `${i.variation.variationName} · SKU ${i.variation.sku} · `
                      : ""}
                    PID {i.product.pid}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {i.quantity} × {formatPrice(i.unitPrice)} ={" "}
                    <b className="text-gray-900">{formatPrice(i.itemTotal)}</b>
                    {i.priceChanged && (
                      <span className="ml-2 text-amber-600">
                        (price changed since added)
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    Added {formatDate(i.addedAt)}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  {i.product.productUrl && (
                    <a
                      href={i.product.productUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-indigo-600 hover:underline"
                    >
                      Source ↗
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => removeItem(i)}
                    disabled={busy === i.id}
                    className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-50"
                  >
                    {busy === i.id ? "Removing…" : "Remove"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

/* ---------- page ---------- */

export const ElevenCarts = (props) => {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [includeEmpty, setIncludeEmpty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);
  const [toast, setToast] = useState(null);

  const notify = useCallback((type, msg) => setToast({ type, msg }), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axiosInstance.get(API.list, {
        params: {
          page,
          search: query || undefined,
          includeEmpty: includeEmpty || undefined,
        },
      });
      setRows(data.carts || []);
      setStats(data.stats);
      setPagination(data.pagination);
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to load carts");
    } finally {
      setLoading(false);
    }
  }, [page, query, includeEmpty]);

  useEffect(() => {
    load();
  }, [load]);

  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] content-start">
      <Toast toast={toast} onClose={() => setToast(null)} />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">
            Customer Carts
          </h1>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Stat label="Active carts" value={stats?.activeCarts ?? "—"} />
        <Stat label="Line items" value={stats?.totalItems ?? "—"} />
        <Stat label="Total quantity" value={stats?.totalQty ?? "—"} />
        <Stat
          label="Value in carts"
          value={stats ? formatPrice(stats.totalValue) : "—"}
          hint="At the price when added"
        />
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 px-3 py-3 border-b border-gray-100">
          <form onSubmit={submitSearch} className="flex gap-2 w-full md:w-auto">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Customer name, email or phone"
              className="w-full md:w-80 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
            />
            <button
              type="submit"
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Search
            </button>
          </form>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={includeEmpty}
              onChange={(e) => {
                setPage(1);
                setIncludeEmpty(e.target.checked);
              }}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            Include empty carts
          </label>
        </div>

        {error ? (
          <div className="px-4 py-10 text-center">
            <p className="text-sm text-red-600">{error}</p>
            <button
              type="button"
              onClick={load}
              className="mt-3 text-sm font-semibold text-indigo-600 hover:underline"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500">
                  <th className="px-3 py-2.5 font-medium">Customer</th>
                  <th className="px-3 py-2.5 font-medium">Items</th>
                  <th className="px-3 py-2.5 font-medium text-right">Qty</th>
                  <th className="px-3 py-2.5 font-medium text-right">Value</th>
                  <th className="px-3 py-2.5 font-medium">Last activity</th>
                  <th className="px-3 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-10 text-center text-gray-400"
                    >
                      Loading…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-10 text-center text-gray-400"
                    >
                      {query
                        ? "No carts match your search."
                        : "No carts with items right now."}
                    </td>
                  </tr>
                ) : (
                  rows.map((c) => (
                    <tr
                      key={c.id}
                      className={`border-t border-gray-100 hover:bg-gray-50 ${loading ? "opacity-60" : ""}`}
                    >
                      <td className="px-3 py-3">
                        <p className="font-medium text-gray-900">
                          {c.user?.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {c.user?.email}
                          {c.user?.phone ? ` · ${c.user.phone}` : ""}
                        </p>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          {c.previewImgs.map((src, i) => (
                            <Img key={i} src={src} className="w-8 h-8" />
                          ))}
                          <span className="ml-1 text-xs text-gray-500">
                            {c.itemCount}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right font-medium text-gray-900">
                        {c.totalQty}
                      </td>
                      <td className="px-3 py-3 text-right font-bold text-gray-900">
                        {formatPrice(c.subtotal)}
                      </td>
                      <td
                        className="px-3 py-3 text-gray-600 whitespace-nowrap"
                        title={formatDate(c.lastActivity)}
                      >
                        {timeAgo(c.lastActivity)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setOpenId(c.id)}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        <Pagination p={pagination} onPage={setPage} disabled={loading} />
      </div>

      <Drawer
        open={Boolean(openId)}
        onClose={() => setOpenId(null)}
        title="Cart details"
      >
        {openId && (
          <CartDetail cartId={openId} onChanged={load} notify={notify} />
        )}
      </Drawer>
    </div>
  );
};

export default ElevenCarts;
